import React from 'react';
import styled from 'styled-components';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faTwitter,
  faLinkedinIn,
  faInstagram,
  faYoutube
} from '@fortawesome/free-brands-svg-icons';
import {
  faEnvelope,
  faPhone,
  faLocationDot
} from '@fortawesome/free-solid-svg-icons';
import logoImage from '../../assets/images/whatzpro-logo.png';

const FooterWrapper = styled.footer`
  background: #1a1c1e;
  color: #ffffff;
  padding: 80px 0 0;
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1.5fr;
  gap: 40px;
  margin-bottom: 60px;
  justify-content: center;

  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
    justify-items: center;
  }

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
  }
`;

const FooterColumn = styled.div`
  @media (max-width: 576px) {
    text-align: center;
  }
`;

const Logo = styled.div`
  margin-bottom: 20px;
  img {
    height: 40px;
    // filter: brightness(0) invert(1);
    transition: opacity 0.3s ease;
    
    &:hover {
      opacity: 0.9;
    }
  }
`;

const CompanyDescription = styled.p`
  color: #a0a0a0;
  line-height: 1.6;
  margin-bottom: 25px;
  font-size: 15px;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 15px;

  @media (max-width: 576px) {
    justify-content: center;
  }
`;

const SocialIcon = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  transition: all 0.3s ease;

  &:hover {
    background: #ff6b6b;
    transform: translateY(-3px);
  }
`;

const ColumnTitle = styled.h4`
  color: white;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 25px;
`;

const QuickLinks = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const QuickLink = styled.li`
  margin-bottom: 12px;
  
  a {
    color: #a0a0a0;
    text-decoration: none;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 5px;

    &:hover {
      color: #ff6b6b;
      transform: translateX(5px);
    }
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  color: #a0a0a0;

  @media (max-width: 576px) {
    justify-content: center;
  }

  svg {
    color: #ff6b6b;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  margin-bottom: 20px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px;
  padding-right: 50px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: white;
  font-size: 14px;

  &::placeholder {
    color: #a0a0a0;
  }

  &:focus {
    outline: none;
    border-color: #ff6b6b;
  }
`;

const SubmitButton = styled.button`
  position: absolute;
  right: 5px;
  top: 50%;
  transform: translateY(-50%);
  background: #ff6b6b;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    background: #ff5252;
  }
`;

const BottomBar = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding: 20px 0;
  text-align: center;
  color: #a0a0a0;
  font-size: 14px;

  a {
    color: #ff6b6b;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (sectionId: string) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const headerOffset = 80;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
  };

  return (
    <FooterWrapper>
      <FooterContent>
        <FooterGrid>
          <FooterColumn>
            <Logo>
              <img src={logoImage} alt="GoHighLevel" />
            </Logo>
            <CompanyDescription>
              Transform your business communication with our powerful WhatsApp Business API solution.
              Reach customers globally and automate your messaging workflow efficiently.
            </CompanyDescription>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Quick Links</ColumnTitle>
            <QuickLinks>
              <QuickLink>
                <a onClick={() => scrollToSection('features')}>Features</a>
              </QuickLink>
              <QuickLink>
                <a onClick={() => scrollToSection('pricing')}>Pricing</a>
              </QuickLink>
              <QuickLink>
                <a onClick={() => scrollToSection('contact')}>Contact</a>
              </QuickLink>
              <QuickLink>
                <Link to="/privacy-policy">Privacy Policy</Link>
              </QuickLink>
              <QuickLink>
                <Link to="/terms-of-service">Terms of Service</Link>
              </QuickLink>
            </QuickLinks>
          </FooterColumn>

          <FooterColumn>
            <ColumnTitle>Contact Info</ColumnTitle>
            <ContactInfo>
              <ContactItem>
                <FontAwesomeIcon icon={faLocationDot} />
                <span>B-801, The First,
                  B/H Keshavbaug, Vastrapur,
                  Ahmedabad, Gujarat 380015</span>
              </ContactItem>
              <ContactItem>
                <FontAwesomeIcon icon={faPhone} />
                <span>+91 7940327195</span>
              </ContactItem>
              <ContactItem>
                <FontAwesomeIcon icon={faEnvelope} />
                <a href="mailto:info@theonetechnologies.com">info@theonetechnologies.com</a>
              </ContactItem>
            </ContactInfo>
          </FooterColumn>
          <FooterColumn>
            <SocialLinks>
              <SocialIcon href="https://www.facebook.com/TheOneTechnologies" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faFacebookF} />
              </SocialIcon>
              <SocialIcon href="https://x.com/TheOne_Tech" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faTwitter} />
              </SocialIcon>
              <SocialIcon href="https://www.linkedin.com/company/the-one-technologies/" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faLinkedinIn} />
              </SocialIcon>
              <SocialIcon href="https://www.instagram.com/theone_technologies" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faInstagram} />
              </SocialIcon>
              <SocialIcon href="https://www.youtube.com/channel/UCw-lD_GPidiKFhyflP0_CwA?rel=0" target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faYoutube} />
              </SocialIcon>
            </SocialLinks>
          </FooterColumn>
        </FooterGrid>
      </FooterContent>

      <BottomBar>
        <FooterContent>
          © {new Date().getFullYear()} WhatzPro. All rights reserved.
        </FooterContent>
      </BottomBar>
    </FooterWrapper>
  );
};

export default Footer; 